import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Loader2, Calendar, Heart, Sun } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Featured seasonal drinks
const featuredSeasonals = [
  {
    id: 'mon-tonia',
    name: 'Mon-Tonia',
    subtitle: 'Huckleberry Lemonade',
    description: "A special drink close to our hearts. This refreshing huckleberry lemonade is a loving tribute to Danni's mom, who hails from the beautiful mountains of Montana. She passed down her love of wild huckleberries to her kids and grandkids, and now we're sharing that love with you. Sweet, tart, and bursting with berry goodness – it's like a taste of Big Sky Country in every sip.",
    image_url: 'https://customer-assets.emergentagent.com/job_sip-schedule-admin/artifacts/w7phvfof_Mon-Tonia.jpg',
    season: 'Family Favorite',
    icon: Heart,
    color: 'from-pink-400 to-pink-600'
  },
  {
    id: 'vacation-milk',
    name: 'Vacation Milk',
    subtitle: 'Tropical Escape',
    description: "Close your eyes and take a sip – can you feel the sand between your toes? Vacation Milk is your instant ticket to relaxation. This dreamy, creamy blend gives you all the vibes of lounging on a tropical beach with palm trees swaying and waves gently rolling in. No passport required, just good vibes and great sips!",
    image_url: 'https://customer-assets.emergentagent.com/job_sip-schedule-admin/artifacts/08how2os_Vacation%20Milk.png',
    season: 'Beach Vibes',
    icon: Sun,
    color: 'from-sip-blue to-cyan-500'
  }
];

export const SeasonalPage = () => {
  const [seasonalItems, setSeasonalItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSeasonal = async () => {
      try {
        const response = await fetch(`${API}/seasonal`);
        const data = await response.json();
        setSeasonalItems(data);
      } catch (error) {
        console.error('Failed to fetch seasonal items:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSeasonal();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="seasonal-page">
      <Navbar />
      
      {/* Header */}
      <section className="bg-gradient-to-r from-sip-pink via-sip-blue to-sip-yellow py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Sparkles className="w-16 h-16 mx-auto mb-4 text-white" />
            <h1 className="font-script text-4xl md:text-6xl text-white mb-4">
              Seasonal Specials
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Limited-time flavors that celebrate the season's best!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Seasonal Drinks */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-script text-3xl md:text-4xl text-sip-navy text-center mb-12"
          >
            Featured This Season
          </motion.h2>
          
          <div className="space-y-16">
            {featuredSeasonals.map((item, index) => {
              const Icon = item.icon;
              const isEven = index % 2 === 0;
              
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
                  data-testid={`featured-${item.id}`}
                >
                  {/* Image */}
                  <div className="lg:w-1/2">
                    <div className="relative">
                      <div className={`absolute inset-0 bg-gradient-to-br ${item.color} rounded-3xl transform rotate-3 opacity-20`} />
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="relative w-full max-w-md mx-auto h-auto rounded-3xl shadow-2xl"
                      />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="lg:w-1/2 text-center lg:text-left">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r ${item.color} text-white text-sm font-medium rounded-full mb-4`}>
                      <Icon size={16} />
                      {item.season}
                    </div>
                    <h3 className="font-script text-4xl md:text-5xl text-sip-navy mb-2">
                      {item.name}
                    </h3>
                    <p className="text-xl text-sip-pink font-medium mb-4">
                      {item.subtitle}
                    </p>
                    <p className="text-gray-600 text-lg leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SeasonalPage;
